/**
 * T-A11：图片空间列表挂载契约（MOUNT_STRICT）。
 * 成功入列项禁止长期「图片地址缺失」：须有受管 url / 签名展示 / 或明确未同步+inline 预览。
 */

import {
  hasImageSpacePersistedUrl,
  isInlineImagePreviewUrl,
} from '@/pages/ipe/workbench/utils/imageSpaceDisplayUrl';
import { UPLOAD_CONFIG } from '@/utils/uploadConfig';

function trimmed(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export function hasImageSpaceSignedDisplay(image) {
  return Boolean(trimmed(image?.viewUrl) || trimmed(image?.displayUrl));
}

export function hasImageSpaceInlinePreview(image) {
  return isInlineImagePreviewUrl(image?.dataUrl)
    || isInlineImagePreviewUrl(image?.src);
}

/**
 * @returns {{ ok: boolean, reason?: string }}
 */
export function assertImageSpaceMountContract(image) {
  if (!image || typeof image !== 'object') {
    return { ok: false, reason: 'invalid' };
  }
  if (hasImageSpacePersistedUrl(image)) {
    return { ok: true };
  }
  if (hasImageSpaceSignedDisplay(image)) {
    return { ok: true };
  }
  if (image.ingestPending === true && hasImageSpaceInlinePreview(image)) {
    return { ok: true };
  }
  if (hasImageSpaceInlinePreview(image)) {
    // 有本机预览但未标 pending：仍可展示；严格模式入列时会补 ingestPending
    return { ok: true, reason: 'inline-only' };
  }
  return { ok: false, reason: 'missing-url' };
}

export function passesImageSpaceMountContract(image) {
  return assertImageSpaceMountContract(image).ok;
}

/**
 * 写入列表前硬化：缺挂载的成功项尽量降为 ingestPending+inline；严格模式下无预览则剔除。
 * @param {Array<object>} items
 * @param {{ mountStrict?: boolean }} [options]
 */
export function enforceImageSpaceMountContract(items, options = {}) {
  const mountStrict = options.mountStrict !== undefined
    ? Boolean(options.mountStrict)
    : UPLOAD_CONFIG.mountStrict !== false;
  const input = Array.isArray(items) ? items : [];
  const accepted = [];
  const rejected = [];

  for (const raw of input) {
    if (!raw || typeof raw !== 'object') {
      rejected.push(raw);
      continue;
    }
    const check = assertImageSpaceMountContract(raw);
    if (check.ok && check.reason !== 'inline-only') {
      accepted.push(raw);
      continue;
    }
    if (hasImageSpaceInlinePreview(raw)) {
      accepted.push({
        ...raw,
        ingestPending: true,
        dataUrl: trimmed(raw.dataUrl) || trimmed(raw.src) || raw.dataUrl || null,
      });
      continue;
    }
    if (mountStrict) {
      rejected.push(raw);
      continue;
    }
    accepted.push(raw);
  }

  return { accepted, rejected, mountStrict };
}

export default {
  assertImageSpaceMountContract,
  passesImageSpaceMountContract,
  enforceImageSpaceMountContract,
  hasImageSpaceSignedDisplay,
  hasImageSpaceInlinePreview,
};
